import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, useIsDesktop } from './reportTokens';
import DesktopLayout from '../layout/DesktopLayout';
import Phone from '../Phone';
import TopBar from '../layout/TopBar';
import BottomNav from '../BottomNav';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { useDesktopTopBar } from '../../context/DesktopLayoutContext';

// ─────────────────────────────────────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────────────────────────────────────

const REPORTS: ReportDoc[] = [
    {
        id: 'snapshot',
        path: '/reports/snapshot',
        icon: 'flash_on',
        color: T.accent,
        colorSoft: 'rgba(210,58,59,0.08)',
        label: 'Snapshot',
        tagline: 'Your at-a-glance dashboard for daily operations',
        summary:
            'The Snapshot report is the home base for every shift. It surfaces the most important KPIs across sales, guests, and staff in a single view — no drilling required. Use it at the start of a day or end of service to get a complete health-check of your outlet.',
        metrics: [
            { icon: 'payments', name: 'Net Sales', desc: 'Total revenue after discounts, before taxes.' },
            { icon: 'receipt_long', name: 'Orders', desc: 'Total number of paid orders in the period.' },
            { icon: 'calculate', name: 'Avg Order Value', desc: 'Net sales divided by total orders.' },
            { icon: 'cancel', name: 'Cancelled Orders', desc: 'Orders that were opened but not paid.' },
            { icon: 'attach_money', name: 'Tips', desc: 'Total tip amount collected from guests.' },
            { icon: 'trending_up', name: 'Sales / Hour', desc: 'Revenue rate across the selected period.' },
            { icon: 'how_to_reg', name: 'Check-Ins', desc: 'Guests who checked in via the host stand.' },
            { icon: 'badge', name: 'Active Staff', desc: 'Staff members who were clocked in.' },
            { icon: 'remove_circle', name: 'Voids', desc: 'Items removed from orders after being placed.' },
            { icon: 'account_balance', name: 'Taxes', desc: 'Total tax collected across all transactions.' },
            { icon: 'local_offer', name: 'Discounts', desc: 'Total value of discounts applied to orders.' },
            { icon: 'group', name: 'Guests', desc: 'Total number of seated guests.' },
            { icon: 'timer', name: 'Avg Wait', desc: 'Average time from check-in to being seated.' },
        ],
        tips: [
            'Pin Snapshot to your morning routine — it gives you the full picture before opening.',
            "Compare today's Sales/Hr trend against your busiest hours to plan staffing.",
            'A spike in Voids may indicate a training gap or item availability issue.',
        ],
        bestFor: ['Morning briefings', 'End-of-shift reviews', 'Quick operational health checks'],
    },
    {
        id: 'sales',
        path: '/reports/sales',
        icon: 'bar_chart',
        color: '#3B82F6',
        colorSoft: 'rgba(59,130,246,0.08)',
        label: 'Sales',
        tagline: 'Detailed revenue breakdown by channel, time, and category',
        summary:
            'The Sales report breaks your revenue down to its components — by dining channel, by hour, by payment type, and by category. It is the go-to report when you need to understand where money is coming from and when your peak windows are.',
        metrics: [
            { icon: 'storefront', name: 'Dine-In Revenue', desc: 'Sales attributed to in-house dining.' },
            { icon: 'takeout_dining', name: 'Takeout Revenue', desc: 'Sales from walk-in takeout orders.' },
            { icon: 'delivery_dining', name: 'Delivery Revenue', desc: 'Sales from delivery channel orders.' },
            { icon: 'schedule', name: 'Hourly Trends', desc: 'Revenue plotted by hour across the day.' },
            { icon: 'category', name: 'Category Sales', desc: 'Revenue contribution per menu category.' },
            { icon: 'credit_card', name: 'Payment Breakdown', desc: 'Split of cash, card, and digital payments.' },
            { icon: 'local_offer', name: 'Discounts Applied', desc: 'Total and per-type discount amounts.' },
            { icon: 'remove_circle', name: 'Voids & Comps', desc: 'Combined value of voided items and comped orders.' },
        ],
        tips: [
            'Use the channel split to evaluate whether your delivery commissions are worth the revenue they bring.',
            'Hourly trends help you decide when to run a happy hour or promotional window.',
            'Cross-reference category sales with Product report data to surface underperforming menu sections.',
        ],
        bestFor: ['Weekly revenue reviews', 'Channel performance analysis', 'Peak hour staffing decisions'],
    },
    {
        id: 'product',
        path: '/reports/product',
        icon: 'restaurant_menu',
        color: T.green,
        colorSoft: 'rgba(22,163,74,0.08)',
        label: 'Product & Menu',
        tagline: 'Item-level performance to drive smarter menu decisions',
        summary:
            'The Product & Menu Performance report goes item-by-item and category-by-category to show what is selling, what is not, and how discounts are being applied. Use it to prune underperformers, boost high-margin winners, and plan menu changes with confidence.',
        metrics: [
            { icon: 'inventory_2', name: 'Top-Selling Items', desc: 'Items ranked by total quantity sold.' },
            { icon: 'attach_money', name: 'Item Revenue', desc: 'Gross revenue generated per item.' },
            { icon: 'pie_chart', name: 'Category Contribution', desc: "Each category's share of total revenue." },
            { icon: 'local_offer', name: 'Item Discount Rate', desc: 'Percentage of orders with discounts on the item.' },
            { icon: 'cancel', name: 'Void Rate', desc: 'How often each item is voided after ordering.' },
            { icon: 'trending_down', name: 'Slow Movers', desc: 'Items with the lowest order frequency in the period.' },
        ],
        tips: [
            'Sort by void rate to find items that guests frequently change their mind about — may indicate a description or quality issue.',
            'Category contribution helps you balance your menu so no single section carries all the revenue risk.',
            'Run this monthly alongside a menu review to keep your offering fresh and profitable.',
        ],
        bestFor: ['Menu engineering sessions', 'Identifying slow-moving stock', 'Pricing reviews'],
    },
    {
        id: 'checkin',
        path: '/reports/checkin',
        icon: 'how_to_reg',
        color: '#0891B2',
        colorSoft: 'rgba(8,145,178,0.08)',
        label: 'Check-In',
        tagline: 'Guest arrivals, wait times, and floor throughput',
        summary:
            'The Check-In report tracks every guest arrival, from the moment they check in at the host stand to when they are seated. It reveals your busiest arrival windows, average wait times, and party size patterns — giving the front-of-house team the data they need to staff and seat more effectively.',
        metrics: [
            { icon: 'how_to_reg', name: 'Total Check-Ins', desc: 'Number of parties that checked in during the period.' },
            { icon: 'group', name: 'Avg Party Size', desc: 'Average number of guests per arriving party.' },
            { icon: 'timer', name: 'Avg Wait Time', desc: 'Average time from check-in to being seated.' },
            { icon: 'schedule', name: 'Peak Arrival Hours', desc: 'Hourly check-in volume across the day.' },
            { icon: 'event_seat', name: 'Seating Efficiency', desc: 'Time taken to move guests from waitlist to table.' },
        ],
        tips: [
            'Use peak arrival data to stagger host-stand staffing — add team members 30 minutes before the rush.',
            'Long wait times on busy days signal a floor layout or table-turn issue, not just volume.',
            'Track party size trends to ensure you have enough large-party configurations available.',
        ],
        bestFor: ['FOH staffing planning', 'Wait time reduction initiatives', 'Service quality reviews'],
    },
    {
        id: 'eod',
        path: '/reports/eod',
        icon: 'credit_card',
        color: '#7C3AED',
        colorSoft: 'rgba(124,58,237,0.08)',
        label: 'EOD Summary',
        tagline: 'End-of-day reconciliation and cash management',
        summary:
            'The EOD (End of Day) Summary report is your daily close-out tool. It consolidates all transactions, payment methods, and cash movements into a single reconciliation view so your manager or accountant can verify that every dollar is accounted for before closing the shift.',
        metrics: [
            { icon: 'payments', name: 'Daily Net Sales', desc: 'Total revenue for the closing day.' },
            { icon: 'point_of_sale', name: 'Cash Collected', desc: 'Total cash received from guests.' },
            { icon: 'credit_card', name: 'Card Payments', desc: 'Total value of card transactions.' },
            { icon: 'wallet', name: 'Digital Payments', desc: 'UPI, wallet, and other digital method totals.' },
            { icon: 'calculate', name: 'Cash Variance', desc: 'Difference between expected and actual cash in drawer.' },
            { icon: 'sync_alt', name: 'Funding Status', desc: "Whether the day's settlement has been processed." },
            { icon: 'receipt', name: 'Tax Summary', desc: 'Breakdown of tax collected by rate.' },
        ],
        tips: [
            'Run EOD immediately after last call every night to catch discrepancies while the shift is still fresh.',
            'A recurring cash variance on the same day or shift may indicate a training issue with a specific staff member.',
            'Use the funding status to confirm payment gateway settlements match your books.',
        ],
        bestFor: ['Daily close procedures', 'Accounting reconciliation', 'Cash management audits'],
    },
    {
        id: 'reservation',
        path: '/reports/reservation',
        icon: 'event_available',
        color: T.orange,
        colorSoft: 'rgba(217,119,6,0.08)',
        label: 'Reservations',
        tagline: 'Booking trends, covers, and no-show analysis',
        summary:
            'The Reservation report gives you a complete view of your bookings pipeline. Track how many covers you are filling, when no-shows are happening, where guests are booking from, and how your floor is being utilised against capacity. Essential for any outlet that takes advance bookings.',
        metrics: [
            { icon: 'event', name: 'Total Reservations', desc: 'Number of bookings made in the period.' },
            { icon: 'people', name: 'Covers', desc: 'Total seated guests via reservations.' },
            { icon: 'cancel', name: 'No-Show Rate', desc: 'Percentage of bookings that did not arrive.' },
            { icon: 'devices', name: 'Booking Source', desc: 'Where guests made the reservation (online, phone, walk-in).' },
            { icon: 'schedule', name: 'Hourly Cover Distribution', desc: 'Covers mapped by hour to show floor load over time.' },
            { icon: 'event_busy', name: 'Cancellations', desc: 'Bookings cancelled before the seating time.' },
        ],
        tips: [
            'A no-show rate above 10% is worth investigating — consider adding a confirmation SMS or deposit policy.',
            'Hourly cover distribution helps you slot walk-in capacity into quieter booking windows.',
            'Check booking source data to see if your online booking channel is being utilised or if most guests still call in.',
        ],
        bestFor: ['Capacity planning', 'No-show reduction strategy', 'Revenue management'],
    },
    {
        id: 'customers',
        path: '/reports/customers',
        icon: 'people',
        color: T.green,
        colorSoft: 'rgba(22,163,74,0.08)',
        label: 'Customers',
        tagline: 'Guest profiles, visit frequency, and lifetime value',
        summary:
            'The Customers report profiles your guest base using visit frequency, spend patterns, and RFM (Recency, Frequency, Monetary) segmentation. It answers the questions: who are your best guests, how often are they coming back, and who is at risk of churning?',
        metrics: [
            { icon: 'person_add', name: 'New Guests', desc: 'First-time visitors in the selected period.' },
            { icon: 'repeat', name: 'Returning Guests', desc: 'Guests who have visited more than once.' },
            { icon: 'schedule', name: 'Avg Visit Frequency', desc: 'Average number of visits per returning guest per month.' },
            { icon: 'attach_money', name: 'Avg Spend per Visit', desc: 'Average transaction value per customer visit.' },
            { icon: 'star', name: 'Top Guests', desc: 'Highest-spend customers ranked by lifetime value.' },
            { icon: 'pie_chart', name: 'RFM Segments', desc: 'Guests grouped by Recency, Frequency, and Monetary value.' },
            { icon: 'trending_down', name: 'At-Risk Guests', desc: 'Guests who have not visited in an unusually long time.' },
        ],
        tips: [
            'Use RFM segments as the audience input for Loyalty campaigns — target "at-risk" guests with a win-back offer.',
            'Track the new-vs-returning ratio weekly. A healthy restaurant typically sees 60–70 % returning guests.',
            'Top guests by lifetime value are your most important relationships — consider a personal outreach or VIP perk.',
        ],
        bestFor: ['Loyalty program targeting', 'Retention campaigns', 'Guest experience reviews'],
    },
    {
        id: 'events',
        path: '/reports/events',
        icon: 'celebration',
        color: T.red,
        colorSoft: 'rgba(220,38,38,0.08)',
        label: 'Events & Pre-Orders',
        tagline: 'Event bookings, pre-order revenue, and pickup slot utilisation',
        summary:
            'The Events report tracks every event booking and its associated pre-orders. Whether you run private dining, ticketed experiences, or pre-order pickups, this report shows how revenue is distributed across events, which slot times are filling fastest, and where capacity gaps exist.',
        metrics: [
            { icon: 'celebration', name: 'Total Events', desc: 'Number of events scheduled in the period.' },
            { icon: 'receipt_long', name: 'Pre-Order Count', desc: 'Number of pre-orders placed across all events.' },
            { icon: 'payments', name: 'Pre-Order Revenue', desc: 'Total value of pre-orders collected.' },
            { icon: 'schedule', name: 'Pickup Slot Utilisation', desc: 'How many slots are filled vs available per time window.' },
            { icon: 'people', name: 'Event Covers', desc: 'Total guests expected across upcoming and past events.' },
            { icon: 'trending_up', name: 'Avg Revenue per Event', desc: 'Mean pre-order value divided by number of events.' },
        ],
        tips: [
            'If certain pickup slots are always empty, consider removing them or incentivising early-slot selection.',
            'Compare pre-order revenue month-over-month to measure the effectiveness of event marketing.',
            'Use this report before planning a new event to understand which day/time formats perform best for your venue.',
        ],
        bestFor: ['Event capacity planning', 'Pre-order revenue forecasting', 'Slot optimisation'],
    },
];

interface MetricDoc {
    icon: string;
    name: string;
    desc: string;
}

interface ReportDoc {
    id: string;
    path: string;
    icon: string;
    color: string;
    colorSoft: string;
    label: string;
    tagline: string;
    summary: string;
    metrics: MetricDoc[];
    tips: string[];
    bestFor: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const MSIcon = ({ name, size = 18, color = T.t2 }: { name: string; size?: number; color?: string }) => (
    <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, color, flexShrink: 0, userSelect: 'none' }}>
        {name}
    </span>
);

const TipBox = ({ tips }: { tips: string[] }) => (
    <div style={{
        background: 'rgba(59,130,246,0.05)',
        border: '1px solid rgba(59,130,246,0.18)',
        borderLeft: '3px solid #3B82F6',
        borderRadius: 8,
        padding: '12px 16px',
        marginTop: 16,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <MSIcon name="lightbulb" size={14} color="#3B82F6" />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tips</span>
        </div>
        <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {tips.map((t, i) => (
                <li key={i} style={{ fontSize: 12.5, color: T.t2, lineHeight: 1.6 }}>{t}</li>
            ))}
        </ul>
    </div>
);

const MetricGrid = ({ metrics }: { metrics: MetricDoc[] }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8, marginTop: 16 }}>
        {metrics.map((m, i) => (
            <div key={i} style={{
                background: T.surface,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 10,
                padding: '10px 14px',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
            }}>
                <div style={{
                    width: 30, height: 30, borderRadius: 8, background: T.hover,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <MSIcon name={m.icon} size={15} color={T.t2} />
                </div>
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 2 }}>{m.name}</div>
                    <div style={{ fontSize: 11.5, color: T.t3, lineHeight: 1.5 }}>{m.desc}</div>
                </div>
            </div>
        ))}
    </div>
);

const BestForRow = ({ items }: { items: string[] }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
        {items.map((item, i) => (
            <span key={i} style={{
                background: T.hover,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 4,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 500,
                color: T.t2,
            }}>
                {item}
            </span>
        ))}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Desktop layout
// ─────────────────────────────────────────────────────────────────────────────

function DesktopDocsPage({ navigate }: { navigate: (path: string) => void }) {
    const setTopBarProps = useDesktopTopBar();

    useEffect(() => {
        setTopBarProps({ title: 'Reports Guide', breadcrumbs: 'Docs' });
    }, [setTopBarProps]);

    return (
        <div
            id="docs-scroll-container"
            style={{ fontFamily: T.font, background: T.bg, padding: '32px 40px', maxWidth: 900, overflowY: 'auto' }}
        >
                {/* Hero */}
                <div style={{
                    background: `linear-gradient(135deg, ${T.accent} 0%, #E86B6B 100%)`,
                    borderRadius: 14,
                    padding: '28px 32px',
                    marginBottom: 40,
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    <div style={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                    <div style={{ position: 'absolute', bottom: -20, right: 60, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MSIcon name="analytics" size={24} color="#fff" />
                        </div>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>Reports Guide</div>
                            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>Everything you need to get the most out of your data</div>
                        </div>
                    </div>
                    <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.7, margin: 0, maxWidth: 580 }}>
                        The Reports section gives you a complete picture of your restaurant's performance — from daily sales and guest flow to long-term customer behaviour. This guide explains what each report does, what metrics it shows, and how to use it effectively.
                    </p>
                </div>

                {/* Getting Started */}
                <section id="section-overview" style={{ marginBottom: 48 }}>
                    <SectionHeading icon="rocket_launch" title="Getting Started" />
                    <p style={{ fontSize: 13.5, color: T.t2, lineHeight: 1.75, marginBottom: 16 }}>
                        Reports are accessible from the left sidebar under <strong style={{ color: T.t1 }}>Reports</strong>. Each report is a dedicated page built around a specific area of your business. All reports support date filtering and refresh automatically when you change the date range.
                    </p>

                    {/* Report overview cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                        {REPORTS.map((r) => (
                            <button
                                key={r.id}
                                onClick={() => navigate(r.path)}
                                style={{
                                    background: T.surface,
                                    border: `1px solid ${T.cardBorder}`,
                                    borderRadius: 12,
                                    padding: '14px 16px',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'flex-start',
                                    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                                    boxShadow: T.cardShadow,
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = r.color + '55';
                                    (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 2px 10px rgba(0,0,0,0.08)`;
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = T.cardBorder;
                                    (e.currentTarget as HTMLButtonElement).style.boxShadow = T.cardShadow;
                                }}
                            >
                                <div style={{
                                    width: 34, height: 34, borderRadius: 9, background: r.colorSoft,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <MSIcon name={r.icon} size={18} color={r.color} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 3 }}>{r.label}</div>
                                    <div style={{ fontSize: 11.5, color: T.t3, lineHeight: 1.5 }}>{r.tagline}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </section>

                {/* Date Filters */}
                <section id="section-filters" style={{ marginBottom: 48 }}>
                    <SectionHeading icon="date_range" title="Using Date Filters" />
                    <p style={{ fontSize: 13.5, color: T.t2, lineHeight: 1.75, marginBottom: 16 }}>
                        Every report has a date filter in the top bar. Select a preset or define a custom range to focus on the period you care about. The report data updates instantly when you change the filter — no page reload required.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                        {[
                            { label: 'Today', desc: 'Live data from the current calendar day.' },
                            { label: 'Yesterday', desc: 'Completed data from the previous day — ideal for morning reviews.' },
                            { label: 'Last 7 Days', desc: 'Rolling weekly view for trend analysis.' },
                            { label: 'Last 30 Days', desc: 'Monthly overview for performance tracking.' },
                            { label: 'This Month', desc: 'Data from the 1st of the current month to today.' },
                            { label: 'Custom Range', desc: 'Pick any start and end date for ad-hoc analysis.' },
                        ].map((f, i) => (
                            <div key={i} style={{
                                background: T.surface,
                                border: `1px solid ${T.cardBorder}`,
                                borderRadius: 10,
                                padding: '12px 14px',
                            }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{f.label}</div>
                                <div style={{ fontSize: 11.5, color: T.t3, lineHeight: 1.5 }}>{f.desc}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{
                        marginTop: 16,
                        background: 'rgba(217,119,6,0.06)',
                        border: '1px solid rgba(217,119,6,0.2)',
                        borderLeft: `3px solid ${T.orange}`,
                        borderRadius: 8,
                        padding: '12px 16px',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                    }}>
                        <MSIcon name="info" size={16} color={T.orange} />
                        <div style={{ fontSize: 12.5, color: T.t2, lineHeight: 1.65 }}>
                            <strong style={{ color: T.t1 }}>Note on Today's data:</strong> Sales figures for the current day reflect transactions up to the moment you load the report. Refresh the page to pull the latest numbers during a live service.
                        </div>
                    </div>
                </section>

                <Divider />

                {/* Per-report sections */}
                {REPORTS.map((r, idx) => (
                    <section key={r.id} id={`section-${r.id}`} style={{ marginBottom: 48 }}>
                        {/* Report header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10,
                                    background: r.colorSoft,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <MSIcon name={r.icon} size={20} color={r.color} />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.t1 }}>{r.label} Report</h2>
                                    <div style={{ fontSize: 12, color: T.t3, marginTop: 2 }}>{r.tagline}</div>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate(r.path)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '7px 14px',
                                    background: r.colorSoft,
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: r.color,
                                    transition: 'opacity 0.15s ease',
                                }}
                                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.75')}
                                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
                            >
                                Open Report
                                <MSIcon name="arrow_forward" size={14} color={r.color} />
                            </button>
                        </div>

                        {/* Route badge */}
                        <div style={{ marginBottom: 14 }}>
                            <code style={{
                                background: T.hover,
                                border: `1px solid ${T.cardBorder}`,
                                borderRadius: 5,
                                padding: '3px 9px',
                                fontSize: 11.5,
                                color: T.t2,
                                fontFamily: "'DM Mono', monospace",
                            }}>
                                {r.path}
                            </code>
                        </div>

                        <p style={{ fontSize: 13.5, color: T.t2, lineHeight: 1.75, margin: '0 0 16px' }}>{r.summary}</p>

                        {/* Best for */}
                        <div style={{ marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: T.t3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Best for</span>
                        </div>
                        <BestForRow items={r.bestFor} />

                        {/* Metrics */}
                        <div style={{ marginTop: 20 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: T.t3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key Metrics</span>
                        </div>
                        <MetricGrid metrics={r.metrics} />

                        {/* Tips */}
                        <TipBox tips={r.tips} />

                        {idx < REPORTS.length - 1 && <Divider style={{ marginTop: 48 }} />}
                    </section>
                ))}

                {/* Footer */}
                <div style={{
                    marginTop: 24,
                    padding: '20px 24px',
                    background: T.surface,
                    border: `1px solid ${T.cardBorder}`,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(210,58,59,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <MSIcon name="support_agent" size={18} color={T.accent} />
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 2 }}>Need help with a specific report?</div>
                        <div style={{ fontSize: 12, color: T.t3 }}>Reach out to your Maghil account manager or use the in-app chat for support.</div>
                    </div>
                </div>

                <div style={{ height: 60 }} />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile layout
// ─────────────────────────────────────────────────────────────────────────────

function MobileDocsPage({ navigate }: { navigate: (path: string) => void }) {
    const [expanded, setExpanded] = useState<string | null>(null);

    return (
        <Phone>
            <TopBar
                variant="mobile"
                title="Reports Guide"
                subtitle="Learn what each report shows"
                onBack={() => navigate('/reports')}
            />
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 24px' }}>

                {/* Intro card */}
                <div style={{
                    background: `linear-gradient(135deg, ${T.accent} 0%, #E86B6B 100%)`,
                    borderRadius: 14,
                    padding: '18px 18px',
                    marginBottom: 20,
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                    <MSIcon name="analytics" size={28} color="#fff" />
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginTop: 8, marginBottom: 4 }}>Reports Guide</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                        Learn what each report tracks and how to use it effectively for your restaurant.
                    </div>
                </div>

                {/* Date filter info */}
                <div style={{
                    background: T.surface,
                    border: `1px solid ${T.cardBorder}`,
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 20,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <MSIcon name="date_range" size={16} color={T.accent} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>Using Date Filters</span>
                    </div>
                    <p style={{ fontSize: 12.5, color: T.t2, lineHeight: 1.65, margin: 0 }}>
                        Every report has a date filter at the top. Choose from Today, Yesterday, 7 Days, 30 Days, This Month, or a custom range. Data updates instantly when you change the filter.
                    </p>
                </div>

                {/* Report cards */}
                <div style={{ fontSize: 11, fontWeight: 700, color: T.t3, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
                    Reports ({REPORTS.length})
                </div>
                {REPORTS.map((r) => {
                    const isOpen = expanded === r.id;
                    return (
                        <div key={r.id} style={{
                            background: T.surface,
                            border: `1px solid ${isOpen ? r.color + '44' : T.cardBorder}`,
                            borderRadius: 12,
                            marginBottom: 8,
                            overflow: 'hidden',
                            transition: 'border-color 0.15s ease',
                        }}>
                            {/* Header row */}
                            <button
                                onClick={() => setExpanded(isOpen ? null : r.id)}
                                style={{
                                    width: '100%',
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '14px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                }}
                            >
                                <div style={{
                                    width: 36, height: 36, borderRadius: 9, background: r.colorSoft,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <MSIcon name={r.icon} size={18} color={r.color} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: T.t1 }}>{r.label}</div>
                                    <div style={{ fontSize: 11.5, color: T.t3, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.tagline}</div>
                                </div>
                                <MSIcon name={isOpen ? 'expand_less' : 'expand_more'} size={18} color={T.t3} />
                            </button>

                            {/* Expanded content */}
                            {isOpen && (
                                <div style={{ borderTop: `1px solid ${T.divider}`, padding: '14px 16px' }}>
                                    <p style={{ fontSize: 13, color: T.t2, lineHeight: 1.7, margin: '0 0 12px' }}>{r.summary}</p>

                                    <div style={{ fontSize: 11, fontWeight: 700, color: T.t3, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                                        Key Metrics
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                                        {r.metrics.map((m, i) => (
                                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                                <MSIcon name={m.icon} size={14} color={T.t3} />
                                                <div>
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{m.name}: </span>
                                                    <span style={{ fontSize: 12, color: T.t3 }}>{m.desc}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => navigate(r.path)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            width: '100%',
                                            justifyContent: 'center',
                                            padding: '10px',
                                            background: r.colorSoft,
                                            border: 'none',
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: r.color,
                                        }}
                                    >
                                        <MSIcon name="open_in_new" size={15} color={r.color} />
                                        Open {r.label} Report
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <BottomNav />
        </Phone>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

const SectionHeading = ({ icon, title }: { icon: string; title: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(210,58,59,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MSIcon name={icon} size={17} color={T.accent} />
        </div>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.t1 }}>{title}</h2>
    </div>
);

const Divider = ({ style = {} }: { style?: React.CSSProperties }) => (
    <hr style={{ border: 'none', borderTop: `1px solid ${T.divider}`, margin: 0, ...style }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────

export default function ReportsDocsPage() {
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();
    useSwipeBack();

    return isDesktop
        ? <DesktopLayout><DesktopDocsPage navigate={navigate} /></DesktopLayout>
        : <MobileDocsPage navigate={navigate} />;
}
